/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    AfterViewInit,
    Directive,
    DOCUMENT,
    ElementRef,
    HostListener,
    inject,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
} from '@angular/core';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import * as _ from 'lodash-es';
import { template } from 'lodash-es';

import { Coordinate } from 'ol/coordinate';
import Event from 'ol/events/Event';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import { unByKey } from 'ol/Observable';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, filter, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppConfigState } from '@core/state/appConfig/app-config.state';
import { MapQuerySpatialFilter } from '@app/types/Map';
import { MapTipConfiguration } from '@app/types/MapTipConfiguration';
import { LoggerService } from '@shared/services/logger.service';
import { MapConfigService } from '../services/map-config.service';
import { MapService } from '../services/map.service';
import { QueryService } from '../services/query.service';
import { DisableMapTip, EnableMapTip, MapCreated, SetMapZoom, ToggleMapTipEnabled } from '../state/active-map.actions';
import { ActiveMapState } from '../state/active-map.state';
import { LayerStateChange } from '../state/map-config.actions';

const MAPTIP_TOGGLE_KEY = '`';

@Directive({ selector: '[csMapTip]' })
export class MapTipDirective implements OnInit, OnDestroy, AfterViewInit {
    public mapTips: any[];

    private tipTimer: any;
    private lastPos: Coordinate;
    private tipLayers: any[];
    private handlerKey: any;
    private mousePosition: Coordinate;
    private mapTipEnabled = true;
    private mapTipElement: HTMLDivElement;
    private tipDefinitions: any[];
    private isMapTipHidden = false;

    private disposeSubject = new Subject<void>();

    private mapTipEnabled$: Observable<boolean> = inject(Store).select(ActiveMapState.mapTipEnabled);
    private initiallyEnabled$: Observable<boolean> = inject(Store).select(AppConfigState.mapTipsInitiallyEnabled);
    private pointerMoveListener: (evt: MouseEvent) => void;

    constructor(
        private logger: LoggerService,
        private host: ElementRef,
        private renderer: Renderer2,
        private mapConfig: MapConfigService,
        private mapService: MapService,
        private queryService: QueryService,
        private actions$: Actions,
        private store: Store,
        private ngZone: NgZone,
        @Inject(DOCUMENT) private document: Document
    ) {
        // setup lodash template to use angular style delimiters
        _.templateSettings.interpolate = /{{([\s\S]+?)}}/g;
    }

    public ngOnInit() {
        this.tipTimer = null;
        this.mapTips = [];
        this.lastPos = [-1, -1];
        /** @member {Array} */
        this.tipLayers = [];

        this.mapService.mapAvailable$
            .pipe(
                filter((b) => b),
                concatMap(() => this.store.select(AppConfigState.mapTips)),
                tap((tipDefinitions: MapTipConfiguration[]) => {
                    this.tipDefinitions = [];
                    tipDefinitions.forEach((tipDef: MapTipConfiguration) => {
                        this.tipDefinitions.push({
                            name: tipDef.name,
                            keys: tipDef.keys,
                            template: tipDef.template,
                            compiled: template(tipDef.template),
                        });
                    });
                }),
                switchMap(() => this.initiallyEnabled$),
                tap((initiallyEnabled) => {
                    if (initiallyEnabled) {
                        this.store.dispatch(new EnableMapTip());
                    } else {
                        this.store.dispatch(new DisableMapTip());
                    }
                }),
                switchMap(() => this.mapTipEnabled$),
                takeUntil(this.disposeSubject.asObservable())
            )
            .subscribe((mapTipEnabled: boolean) => {
                if (mapTipEnabled) {
                    this.enableMapTip();
                } else {
                    this.disableMapTip();
                }
            });

        this.actions$.pipe(ofActionSuccessful(MapCreated, SetMapZoom)).subscribe(() => this.onMapChange());

        this.actions$.pipe(ofActionSuccessful(LayerStateChange)).subscribe(() => {
            this.onMapChange();
        });
    }

    public ngOnDestroy() {
        this.disposeSubject.next();
        if (this.document) {
            this.document.removeEventListener('pointermove', this.pointerMoveListener);
        }
    }

    public ngAfterViewInit() {
        if (this.document) {
            this.pointerMoveListener = this.getMaptipPositionHandler();
            this.document.addEventListener('pointermove', this.pointerMoveListener, {
                passive: true,
            });
        }

        // create the maptip element
        this.mapTipElement = this.renderer.createElement('div');
        this.renderer.addClass(this.mapTipElement, 'maptip');
        this.renderer.addClass(this.mapTipElement, 'no-maptip');
        this.renderer.appendChild(this.host.nativeElement, this.mapTipElement);
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if ((event.target as Element).tagName.toLowerCase() === 'input') {
            return;
        }

        if (event.key === MAPTIP_TOGGLE_KEY) {
            this.store.dispatch(new ToggleMapTipEnabled());
        }
    }

    getMaptipPositionHandler() {
        const mapTipOffset = 15;
        return (evt: MouseEvent) => {
            if (this.mapTipEnabled) {
                this.ngZone.runOutsideAngular(() => {
                    // see if the mouse is over the map
                    const target = evt.target as HTMLElement;
                    if (!target.matches('.ol-viewport .ol-layer, .ol-viewport canvas')) {
                        this.hideMapTip();
                        return;
                    }

                    this.isMapTipHidden = false;

                    const rect = (evt.target as HTMLElement).getBoundingClientRect();
                    const offsetWidth = (evt.target as HTMLElement).offsetWidth;
                    const offsetHeight = (evt.target as HTMLElement).offsetHeight;
                    const x = evt.offsetX * (rect.width / offsetWidth);
                    const y = evt.offsetY * (rect.height / offsetHeight);

                    // check if close past right edge
                    if (x > rect.width - 250) {
                        this.renderer.setStyle(this.mapTipElement, 'right', `${rect.width - (x - mapTipOffset)}px`);
                        this.renderer.setStyle(this.mapTipElement, 'left', 'unset');
                    } else {
                        this.renderer.setStyle(this.mapTipElement, 'left', `${x + mapTipOffset}px`);
                        this.renderer.setStyle(this.mapTipElement, 'right', 'unset');
                    }

                    // check if close past right edge
                    if (y > rect.height - 250) {
                        this.renderer.setStyle(this.mapTipElement, 'bottom', `${rect.height - (y - mapTipOffset)}px`);
                        this.renderer.setStyle(this.mapTipElement, 'top', 'unset');
                    } else {
                        this.renderer.setStyle(this.mapTipElement, 'top', `${y + mapTipOffset}px`);
                        this.renderer.setStyle(this.mapTipElement, 'bottom', 'unset');
                    }
                });
            }
        };
    }

    private enableMapTip() {
        if (this.handlerKey) {
            this.disableMapTip();
        }
        if (this.mapService.map) {
            this.handlerKey = this.mapService.map.on('pointermove', (e) => {
                this.mapTipHandler(e);
            });
            this.mapTipEnabled = true;
        }
    }

    private disableMapTip() {
        this.mapTips.length = 0; // delete all array items
        if (this.handlerKey && this.handlerKey.type) {
            unByKey(this.handlerKey);
            this.handlerKey = null;
        }
        if (this.tipTimer) {
            clearTimeout(this.tipTimer);
        }
        this.hideMapTip();
        this.mapTipEnabled = false;
    }

    private updateMapTipLayers() {
        this.tipLayers = [];
        const scale = this.mapService.currentScale;
        const visibleLayers = this.mapConfig.getVisibleLayerList(scale);
        this.tipDefinitions.forEach((item: MapTipConfiguration) => {
            const foundLayer = visibleLayers.find((layerConfig) => layerConfig.name === item.name);
            if (foundLayer) {
                this.tipLayers.push(item.name);
            }
        });
    }

    private onMapChange() {
        this.updateMapTipLayers();
        // if (this.tipLayers.length > 0) {
        //     this.mapTipEnabled = true;
        // }
    }

    /**
     * @param evt
     */
    private mapTipHandler(evt: Event) {
        const mapEvent = evt as MapBrowserEvent<MouseEvent>;
        const pos = [this.lastPos[0], this.lastPos[1]];
        this.lastPos = [mapEvent.pixel[0], mapEvent.pixel[1]];
        if (pos[0] === -1 || pos[1] === -1 || (pos[0] === mapEvent.pixel[0] && pos[1] === mapEvent.pixel[1])) {
            return;
        }

        if (mapEvent.dragging) {
            return;
        }

        if (this.tipTimer) {
            clearTimeout(this.tipTimer);
        }

        this.tipTimer = setTimeout(
            (e) => {
                this.showMapTip(e);
                this.tipTimer = null;
            },
            50,
            evt
        );
    }

    private showMapTip(e: any) {
        if (!this.mapTipEnabled || this.isMapTipHidden) {
            return;
        }
        if (this.tipLayers.length === 0) {
            return;
        }
        this.mousePosition = e.pixel;

        const bbox: [number, number, number, number] = [
            e.coordinate[0] - 0.0001,
            e.coordinate[1] - 0.0001,
            e.coordinate[0] + 0.0001,
            e.coordinate[1] + 0.0001,
        ];
        const bboxWithVariance: [number, number, number, number] = [
            e.coordinate[0] - this.variance,
            e.coordinate[1] - this.variance,
            e.coordinate[0] + this.variance,
            e.coordinate[1] + this.variance,
        ];

        const spatialFilter: MapQuerySpatialFilter = {
            filter: {
                bbox: bboxWithVariance,
                type: 'bbox',
            },
            type: 'spatial',
        };
        const withVariance$ = this.queryService
            .getWmsFeatureData$(this.tipLayers, spatialFilter, ['line', 'point'])
            .pipe(
                catchError((err) => {
                    this.logger.error(err);
                    return of([]);
                })
            );

        spatialFilter.filter.bbox = bbox;
        const withoutVariance$ = this.queryService.getWmsFeatureData$(this.tipLayers, spatialFilter, ['polygon']).pipe(
            catchError((err) => {
                this.logger.error(err);
                return of([]);
            })
        );
        forkJoin([withVariance$, withoutVariance$])
            .pipe(
                map((responses) => [].concat.apply([], responses)),
                map((features) => {
                    this.mapTips = [];
                    features.forEach((feature) => {
                        const tipLayer = (feature.getId() as string).split('.')[0];
                        const definition = this.tipDefinitions.find((def) => def.name === tipLayer);
                        const tipData = {};
                        definition.keys.forEach((key) => {
                            tipData[key] = feature.get(key);
                        });
                        const tipHtml = definition.compiled(tipData);
                        this.mapTips.push(tipHtml);
                    });
                }),
                first()
            )
            .subscribe(() => {
                if (this.mapTipEnabled && this.mapTips.length > 0) {
                    this.renderer.removeClass(this.mapTipElement, 'no-maptip');
                    this.renderer.setProperty(this.mapTipElement, 'innerHTML', this.mapTips.join('<br />'));
                } else {
                    this.renderer.addClass(this.mapTipElement, 'no-maptip');
                }
            });
    }

    private hideMapTip() {
        this.isMapTipHidden = true;
        if (this.mapTipElement) {
            this.renderer.addClass(this.mapTipElement, 'no-maptip');
        }
    }

    get variance() {
        return this.store.selectSnapshot(AppConfigState.mapTipVariance);
    }
}
